import { type DocNode } from '@atlaskit/adf-schema';

import { parseLuminaTagsToJSON } from '../layout/parser/tag-parser';
import type { ParsedLayout } from '../layout/types';

import { luminaToAdfJsonNode } from './lumina-to-adf-json-node';
import { convertSection } from './section-to-adf-nodes';

/**
 * Converts parsed Lumina layout to ADF JSON content array.
 * Sections are parsed into arrays (header + layout section), which are flattened.
 * Note: Type assertion is needed due to complex union types in ADF schema
 */
const convertLuminaJsonToAdfContent = (luminaJson: ParsedLayout): DocNode['content'] => {
	const content: any[] = [];

	for (const section of luminaJson) {
		const parsedNodes = convertSection(section, luminaToAdfJsonNode);
		// convertSection returns an array (header + layout section), so we flatten it
		for (const node of parsedNodes) {
			if (node) {
				content.push(node);
			}
		}
	}

	return content as DocNode['content'];
};

/**
 * Converts a Lumina schema to ADF JSON document.
 *
 * @param luminaSchema - The Lumina schema to convert (can be a string or parsed Lumina structure)
 * @returns ADF JSON document (DocNode)
 */
export const luminaToAdf = (luminaSchema: string | ParsedLayout): DocNode => {
	let luminaJson: ParsedLayout;

	// Parse lumina string if needed, otherwise use the provided parsed layout
	if (typeof luminaSchema === 'string') {
		luminaJson = parseLuminaTagsToJSON(luminaSchema);
	} else {
		luminaJson = luminaSchema;
	}

	const content = convertLuminaJsonToAdfContent(luminaJson);

	return {
		version: 1,
		type: 'doc',
		content,
	};
};
